import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Avatar,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridColumnVisibilityModel } from '@mui/x-data-grid';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import { ProviderType, ProviderStatus } from '../types/provider';
import CreateProviderDialog from '../components/CreateProviderDialog';
import PageHeader from '../components/PageHeader';
import { resolveProviderLogoUrl } from '../utils/resolveProviderLogoUrl';
import { useIsMobileMd } from '../hooks/useIsMobile';

const ProvidersPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isMobile = useIsMobileMd();
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<ProviderType | ''>('');
  const [statusFilter, setStatusFilter] = useState<ProviderStatus | ''>('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({});

  React.useEffect(() => {
    setColumnVisibilityModel({
      private_announcements: !isMobile,
      address: !isMobile,
      listings_breakdown: true,
      monthly_fee: !isMobile,
      created_at: !isMobile,
    });
  }, [isMobile]);

  const { data: providersData, isLoading, error } = useQuery({
    queryKey: ['providers', { search: searchTerm, type: typeFilter, status: statusFilter }],
    queryFn: () => apiService.getProviders({
      search: searchTerm || undefined,
      type: typeFilter || undefined,
      status: statusFilter || undefined,
      limit: 100,
    }),
  });

  const { data: providerTypes } = useQuery({
    queryKey: ['provider-types'],
    queryFn: () => apiService.getProviderTypes(),
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'pending_approval':
        return 'warning';
      case 'suspended':
        return 'error';
      default:
        return 'info';
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Nom',
      flex: 1.6,
      minWidth: isMobile ? 140 : 160,
      renderCell: (params) => {
        const logoSrc = resolveProviderLogoUrl(params.row.logo_url, params.row.logo);
        return (
        <Box display="flex" alignItems="center" gap={1} minWidth={0}>
          <Avatar 
            src={logoSrc || undefined}
            sx={{ 
              bgcolor: logoSrc ? 'transparent' : 'primary.light', 
              width: 32, 
              height: 32,
              flexShrink: 0,
              border: logoSrc ? '1px solid' : 'none',
              borderColor: logoSrc ? 'divider' : 'transparent',
            }}
            variant="rounded"
          >
            {!logoSrc && (params.value?.charAt(0)?.toUpperCase() ?? '?')}
          </Avatar>
          <Box minWidth={0}>
            <Typography variant="subtitle2" fontWeight="bold" noWrap title={params.value}>
              {params.value}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap title={params.row.formatted_type}>
              {params.row.formatted_type}
            </Typography>
          </Box>
        </Box>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Statut',
      flex: 0.9,
      minWidth: 110,
      renderCell: (params) => (
        <Chip
          label={params.row.formatted_status}
          size="small"
          color={getStatusColor(params.value) as any}
        />
      ),
    },
    {
      field: 'private_announcements',
      headerName: 'Visibilité',
      flex: 0.9,
      minWidth: 120,
      renderCell: (params) => (
        <Box display="flex" flexDirection="column" gap={0.5}>
          {params.row.visible_on_frontend === false ? (
            <Chip label="Masqué site" size="small" color="default" variant="outlined" />
          ) : (
            <Chip label="Sur le site" size="small" color="success" variant="outlined" />
          )}
          {params.row.private_announcements ? (
            <Chip label="Annonces privées" size="small" color="warning" variant="outlined" />
          ) : null}
        </Box>
      ),
    },
    {
      field: 'address',
      headerName: 'Localisation',
      flex: 0.55,
      minWidth: 72,
      maxWidth: 110,
      renderCell: (params) => (
        <Box minWidth={0} overflow="hidden">
          <Typography variant="body2" noWrap title={params.value.city}>
            {params.value.city}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap title={params.value.region}>
            {params.value.region}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'listings_breakdown',
      headerName: 'Annonces / Objets',
      flex: isMobile ? 1.1 : 1.35,
      minWidth: isMobile ? 120 : 168,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const linked =
          params.row.classic_active_announcements_count ??
          Math.max(
            0,
            (params.row.active_announcements_count ?? 0) - (params.row.found_objects_active_count ?? 0),
          );
        const foundObjects =
          params.row.found_objects_active_count ?? params.row.found_objects_count ?? 0;
        const total = params.row.active_announcements_count ?? linked + foundObjects;

        return (
          <Box minWidth={0} lineHeight={1.25} py={0.5}>
            <Typography variant="caption" color="text.secondary" display="block" noWrap title="Objets trouvés partenaires (hors disposés)">
              Objets trouvés : <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{foundObjects}</Box>
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" noWrap title="Annonces utilisateurs liées (actives, hors FO-CONTACT)">
              Annonces liées : <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{linked}</Box>
            </Typography>
            {!isMobile && (
              <Typography variant="caption" color="text.disabled" display="block" noWrap>
                Total : {total}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'monthly_fee',
      headerName: 'Abonnement',
      flex: 0.7,
      minWidth: 88,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="bold" noWrap>
          {params.value ? `${params.value}€/mois` : 'Gratuit'}
        </Typography>
      ),
    },
    {
      field: 'created_at',
      headerName: 'Créé le',
      flex: 0.55,
      minWidth: 82,
      renderCell: (params) => (
        <Typography variant="body2" noWrap>
          {new Date(params.value).toLocaleDateString('fr-FR')}
        </Typography>
      ),
    },
  ];

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        Erreur lors du chargement des partenaires
      </Alert>
    );
  }

  return (
    <Box>
      <PageHeader
        title={
          <Typography variant="h4" component="h1" gutterBottom>
            Gestion des Partenaires
          </Typography>
        }
        subtitle={
          <Typography variant="body1" color="text.secondary">
            Gérez les partenaires de lieu de la plateforme
          </Typography>
        }
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Ajouter un partenaire
          </Button>
        }
      />

      {/* Filtres */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Rechercher"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={typeFilter}
                  label="Type"
                  onChange={(e) => setTypeFilter(e.target.value as ProviderType)}
                >
                  <MenuItem value="">Tous</MenuItem>
                  {providerTypes?.types && Object.entries(providerTypes.types).map(([key, type]) => (
                    <MenuItem key={key} value={key}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Statut</InputLabel>
                <Select
                  value={statusFilter}
                  label="Statut"
                  onChange={(e) => setStatusFilter(e.target.value as ProviderStatus)}
                >
                  <MenuItem value="">Tous</MenuItem>
                  <MenuItem value="active">Actif</MenuItem>
                  <MenuItem value="pending_approval">En attente</MenuItem>
                  <MenuItem value="inactive">Inactif</MenuItem>
                  <MenuItem value="suspended">Suspendu</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                {providersData?.pagination.total || 0} partenaire(s) trouvé(s)
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tableau des partenaires */}
      <Card>
        <CardContent sx={{ p: 0, maxWidth: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <DataGrid
            rows={providersData?.providers || []}
            columns={columns}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={setColumnVisibilityModel}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 25 },
              },
            }}
            pageSizeOptions={[25, 50, 100]}
            disableRowSelectionOnClick
            disableColumnMenu
            autoHeight
            getRowHeight={() => 'auto'}
            onRowClick={(params) => navigate(`/providers/${params.id}`)}
            sx={{
              border: 'none',
              width: '100%',
              minWidth: isMobile ? 0 : undefined,
              cursor: 'pointer',
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid #f0f0f0',
                py: 1,
                alignItems: 'flex-start',
              },
              '& .MuiDataGrid-columnHeader': {
                alignItems: 'flex-end',
              },
            }}
          />
        </CardContent>
      </Card>

      {/* Dialog de création */}
      <CreateProviderDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={() => {
          setCreateDialogOpen(false);
          queryClient.invalidateQueries({ queryKey: ['providers'] });
          queryClient.invalidateQueries({ queryKey: ['provider-statistics'] });
        }}
      />

    </Box>
  );
};

export default ProvidersPage;

